"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isValidUrl = exports.isValidEmail = void 0;
exports.useForm = useForm;
const email_validator_1 = require("email-validator");
const react_1 = require("react");
function useForm({ onSubmit, onFormChange, initialValues, validateFields, }) {
    const mounted = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const validationRules = (0, react_1.useRef)(validateFields);
    validationRules.current = validateFields;
    const [form, setForm] = (0, react_1.useState)(Object.assign({}, initialValues));
    const [errors, setErrors] = (0, react_1.useState)({});
    const [isSubmittingForm, setIsSubmittingForm] = (0, react_1.useState)(false);
    const handleFormSubmit = (0, react_1.useCallback)(async (e) => {
        e.preventDefault();
        const { errors: validatedErrors, isFormValid } = validateForm(form, validationRules.current);
        if (isFormValid) {
            setIsSubmittingForm(true);
            try {
                await Promise.resolve(onSubmit(form));
            }
            finally {
                if (mounted.current) {
                    setIsSubmittingForm(false);
                }
            }
        }
        if (mounted.current) {
            setErrors(validatedErrors);
        }
    }, [validateForm, form, onSubmit, setIsSubmittingForm, setErrors]);
    const updateForm = (0, react_1.useCallback)((name, value) => {
        setForm((prevForm) => {
            const updatedForm = Object.assign({}, prevForm, { [name]: value });
            onFormChange?.(updatedForm);
            return updatedForm;
        });
        setErrors((oldErrors) => Object.assign({}, oldErrors, { [name]: undefined }));
    }, [onFormChange, setForm, setErrors]);
    const reset = (0, react_1.useCallback)((form) => {
        setForm(form);
        if (mounted.current) {
            setErrors(validateForm(form, validationRules.current).errors);
        }
    }, []);
    const handleChange = (0, react_1.useCallback)((e) => {
        if (isValueEvent(e)) {
            return updateForm(e.name, e.value);
        }
        const event = isCommonUIEvent(e) ? e.event : e;
        const { currentTarget } = event;
        if (currentTarget instanceof HTMLInputElement && currentTarget.type === 'checkbox') {
            return updateForm(currentTarget.name, currentTarget.checked);
        }
        if (currentTarget instanceof HTMLInputElement && currentTarget.type === 'number') {
            const value = currentTarget.value;
            const valueNr = Number(value);
            if (isNaN(valueNr)) {
                return;
            }
            return updateForm(currentTarget.name, valueNr);
        }
        return updateForm(currentTarget.name, currentTarget.value);
    }, [updateForm]);
    return (0, react_1.useMemo)(() => ({
        form,
        errors,
        onChange: handleChange,
        onSubmit: handleFormSubmit,
        reset,
        isSubmittingForm,
    }), [form, errors, handleChange, handleFormSubmit, isSubmittingForm]);
}
// Typeguard used to handle events from custom components
function isValueEvent(event) {
    if (event.name === undefined || event.value === undefined) {
        return false;
    }
    const value = event.value;
    return typeof value === 'string' || typeof value === 'boolean' || !Number.isNaN(value) || Array.isArray(value);
}
function isCommonUIEvent(event) {
    return event.event !== undefined;
}
const validateForm = (form, validationFields) => {
    if (validationFields === undefined) {
        return {
            isFormValid: true,
            errors: {},
        };
    }
    const formFieldNames = Object.keys(form);
    const errors = formFieldNames.reduce((memo, formFieldName) => {
        const value = form[formFieldName];
        const validationType = validationFields[formFieldName];
        if (validationType === undefined) {
            return memo;
        }
        const nonEmptyValidation = validationType['exist'];
        const emailValidation = validationType['email'];
        const urlValidation = validationType['url'];
        const customValidation = validationType['custom'];
        if (nonEmptyValidation !== undefined) {
            if (value === '' || value === null || value === undefined) {
                memo[formFieldName] = nonEmptyValidation;
                return memo;
            }
        }
        if (customValidation !== undefined && !customValidation.condition(value)) {
            memo[formFieldName] = customValidation.error;
            return memo;
        }
        if (emailValidation !== undefined && !(0, exports.isValidEmail)(value)) {
            memo[formFieldName] = emailValidation;
            return memo;
        }
        if (urlValidation !== undefined && !(0, exports.isValidUrl)(value)) {
            memo[formFieldName] = urlValidation;
            return memo;
        }
        return memo;
    }, {});
    return {
        isFormValid: Object.keys(errors).length === 0,
        errors,
    };
};
const isValidEmail = (email) => {
    if (typeof email !== 'string') {
        return false;
    }
    return (0, email_validator_1.validate)(email);
};
exports.isValidEmail = isValidEmail;
const isValidUrl = (url) => {
    if (typeof url !== 'string') {
        return false;
    }
    const regex = new RegExp(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g);
    return regex.test(url);
};
exports.isValidUrl = isValidUrl;
//# sourceMappingURL=use_form.js.map